import { showUpdateToast } from './update-toast.js';

// This module is imported unconditionally by sw-register.js, which itself must stay
// import-safe in insecure contexts (plain HTTP, some private-browsing modes) where
// `navigator.serviceWorker` does not exist at all — evaluating this file must never throw.
const hasServiceWorker = typeof navigator !== 'undefined' && navigator.serviceWorker != null;
const hadControllerAtBoot = hasServiceWorker && navigator.serviceWorker.controller !== null;

export function attachUpdateListener(registration) {
  if (!hasServiceWorker) return;

  function promptReload() {
    showUpdateToast({
      onReload() {
        const worker = registration.waiting;
        if (worker !== null) {
          worker.postMessage({ type: 'SKIP_WAITING' });
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            location.reload();
          }, { once: true });
        } else {
          location.reload();
        }
      },
      onDismiss() {},
    });
  }

  if (registration.waiting && hadControllerAtBoot) {
    promptReload();
  }

  function attachStateListener(installingWorker) {
    installingWorker.addEventListener('statechange', () => {
      if (installingWorker.state === 'installed' && hadControllerAtBoot) {
        promptReload();
      }
    });
  }

  if (registration.installing) {
    attachStateListener(registration.installing);
  }

  registration.addEventListener('updatefound', () => {
    const installingWorker = registration.installing;
    if (installingWorker) {
      attachStateListener(installingWorker);
    }
  });
}

/**
 * Nudge the browser to check for a new service-worker version so an already-open
 * tab surfaces the update toast without a manual reload. The browser only
 * auto-checks on an in-scope navigation or roughly every 24h — with hash routing
 * that is effectively never — so we call registration.update() whenever the tab
 * regains focus or becomes visible (the common "switch back after a deploy"
 * case), on each in-app hash navigation, plus a slow interval as a fallback for
 * tabs left focused. A detected
 * update flows through the same registration's `updatefound` → `installed`
 * transition that {@link attachUpdateListener} is already watching, so the toast
 * fires on its own. Errors (e.g. offline) are swallowed.
 *
 * @param {ServiceWorkerRegistration} registration
 * @param {{ intervalMs?: number }} [opts]
 * @returns {ReturnType<typeof setInterval>|undefined}
 */
export function startUpdatePolling(registration, { intervalMs = 60_000 } = {}) {
  if (!hasServiceWorker) return undefined;

  function check() {
    if (document.visibilityState === 'hidden') return;
    Promise.resolve(registration.update()).catch(() => {});
  }
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', check);
  // Hash-route navigation is not a page load, so the browser never re-checks
  // the SW on its own when the user clicks around the SPA. Treat each in-app
  // navigation as a cheap opportunity to notice a fresh deploy.
  window.addEventListener('hashchange', check);
  const timer = setInterval(check, intervalMs);
  // Node's timer exposes unref(); the browser's numeric id does not. Unref so
  // the interval never keeps a (test) process alive on its own.
  if (typeof timer === 'object' && timer !== null && typeof timer.unref === 'function') {
    timer.unref();
  }
  return timer;
}
